import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { toneClasses } from './airport-tokens';
import { PhaseChip, ServiceIcon } from './pnr-card-ui';
import type { CardView } from './pnr-band';
import { PHASE_TONE, type TripPhase } from './pnr-steps';
import type { PnrService } from './service-look';

// The passenger's trip IN ORDER (PO 2026-10-31): original flight → wait → hotel → new
// flight → arrival, each with its value and ONE word of the card's vocabulary. A cell
// that needs a decision is tinted; a cell with a service opens that service's view and
// carries its icon in its hue — the same as its card below (service-look.ts).

export interface TripCell {
  key: 'original' | 'wait' | 'hotel' | 'flight' | 'arrival';
  label: string;
  value: string;
  phase: TripPhase;
  /** Overrides the vocabulary word (e.g. «En 13 h» on the arrival). */
  chipLabel?: string | undefined;
  /** The view a click opens. */
  view?: CardView | undefined;
  /** The service the cell is — its icon beside the label. */
  service?: PnrService | undefined;
}

export function PnrTripStrip({ cells, onOpen }: { cells: readonly TripCell[]; onOpen: (view: CardView) => void }) {
  const { t } = useTranslation('pnr');
  return (
    <ol
      className={cn(
        'mx-4 mt-3 grid shrink-0 grid-cols-2 overflow-hidden rounded-xl border border-border bg-muted/30 sm:mx-6 sm:grid-cols-3',
        cells.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4',
      )}
      aria-label={t('card.trip.ariaLabel')}
      data-testid="pnr-trip-strip"
    >
      {cells.map((c) => {
        const decide = c.phase === 'needsDecision';
        const body = (
          <>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {c.service ? <ServiceIcon service={c.service} /> : null}
              {c.label}
            </span>
            <span className="truncate text-[15px] font-bold" data-testid={`pnr-trip-${c.key}-value`}>
              {c.value}
            </span>
            <PhaseChip phase={c.phase} label={c.chipLabel} testId={`pnr-trip-${c.key}-phase`} />
          </>
        );
        const cellCls = cn(
          'flex min-w-0 flex-col gap-1 border-b border-r border-border px-3.5 py-3 text-left',
          decide && toneClasses(PHASE_TONE.needsDecision).surface,
        );
        return (
          <li key={c.key} className="contents" data-testid={`pnr-trip-${c.key}`}>
            {c.view ? (
              <button
                type="button"
                className={cn(cellCls, 'transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring')}
                onClick={() => onOpen(c.view!)}
                aria-label={t('card.trip.open', { step: c.label, value: c.value })}
                data-testid={`pnr-trip-${c.key}-open`}
              >
                {body}
              </button>
            ) : (
              <div className={cellCls}>{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
